import type {
    SearchFilterOption,
    ResultFilterOption,
    SearchResultItem,
} from 'owa-timestream-asset-search';
import type Message from 'owa-service/lib/contract/Message';
import {
    filterLabel_allMessages,
    filterLabel_unread,
    filterLabel_flagged,
    filterLabel_atMentions,
    filterLabel_hasAttachments,
} from '../mailItem.locstring.json';
import {
    searchFilterLabel_searchIn,
    searchFilterLabel_dateFrom,
    searchFilterLabel_dateTo,
    searchFilterLabel_hasAttachment,
    searchFilterLabel_deletedItems,
} from 'owa-locstrings/lib/strings/searchFilterLabel.locstring.json';
import {
    getSearchScopeList,
    primaryMailboxSearchScope,
    PrimaryMailboxSearchScope,
    SearchScopeKind,
} from 'owa-search-service';
import { PRIMARY_FOLDER_ROOT_DISTINGUISHED_ID } from 'owa-folders-constants';
import folderNameToId from 'owa-session-store/lib/utils/folderNameToId';
import getSearchScopeDisplayName from './getSearchScopeDisplayName';
import loc from 'owa-localize';
import { subYears, today } from 'owa-datetime';

export const MailAssetFilterResultOption = {
    AllMessage: 'AllMessage',
    Unread: 'Unread',
    ToMe: 'ToMe',
    Flagged: 'Flagged',
    AtMentions: 'AtMentions',
    Attachments: 'Attachments',
};

export const MailAssetFilterSearchOption = {
    ToDate: 'ToDate',
    FromDate: 'FromDate',
    Folders: 'Folders',
    DeletedItems: 'DeletedItems',
    Attachments: 'Attachments',
};

export function getSearchFilterOptions(): SearchFilterOption[] {
    const searchScope = primaryMailboxSearchScope({
        folderId: folderNameToId(PRIMARY_FOLDER_ROOT_DISTINGUISHED_ID),
        kind: SearchScopeKind.PrimaryMailbox,
    });
    const folders = getSearchScopeList(searchScope);
    const folderOptions = (folders as PrimaryMailboxSearchScope[])
        .filter(f => !!f.folderId)
        .map(folder => {
            return {
                key: folder.folderId,
                text: getSearchScopeDisplayName(folder),
            };
        });

    return [
        {
            id: MailAssetFilterSearchOption.Folders,
            label: loc(searchFilterLabel_searchIn),
            type: 'Dropdown',
            options: folderOptions,
        },
        {
            id: MailAssetFilterSearchOption.FromDate,
            label: loc(searchFilterLabel_dateFrom),
            type: 'DatePicker',
            value: subYears(today(), 1),
        },
        {
            id: MailAssetFilterSearchOption.ToDate,
            label: loc(searchFilterLabel_dateTo),
            type: 'DatePicker',
            value: today(),
        },
        {
            id: MailAssetFilterSearchOption.Attachments,
            label: loc(searchFilterLabel_hasAttachment),
            type: 'Checkbox',
        },
        {
            id: MailAssetFilterSearchOption.DeletedItems,
            label: loc(searchFilterLabel_deletedItems),
            type: 'Checkbox',
        },
    ];
}

export function getResultFilterOptions(): ResultFilterOption[] {
    return [
        {
            id: MailAssetFilterResultOption.AllMessage,
            label: loc(filterLabel_allMessages),
            filter: filterAllMessages,
            hideSelectionPill: true,
        },
        {
            id: MailAssetFilterResultOption.Unread,
            label: loc(filterLabel_unread),
            filter: filterUnread,
        },
        // TODO- there isn't a "to me" attribute on search results yet, so we need to implement this separately
        // {
        //     id: MailAssetFilterOption.ToMe,
        //     label: filterLabel_toMe(),
        //     filter: filterAllMessages,
        // },
        {
            id: MailAssetFilterResultOption.Flagged,
            label: loc(filterLabel_flagged),
            filter: filterFlagged,
        },
        {
            id: MailAssetFilterResultOption.AtMentions,
            label: loc(filterLabel_atMentions),
            filter: filterAtMentions,
        },
        {
            id: MailAssetFilterResultOption.Attachments,
            label: loc(filterLabel_hasAttachments),
            filter: filterHasAttachments,
        },
    ];
}

function filterAllMessages(asset: SearchResultItem): boolean {
    return true;
}

function filterUnread(asset: SearchResultItem): boolean {
    const message = asset.data as Message;
    return !message.IsRead;
}

// function filterToMe(asset: SearchResultItem): boolean {
//     // not yet implemented
//     return true;
// }

function filterFlagged(asset: SearchResultItem): boolean {
    const message = asset.data as Message;
    return !!message.Flag;
}

function filterAtMentions(asset: SearchResultItem): boolean {
    const message = asset.data as Message;
    return !!message.MentionedMe;
}

function filterHasAttachments(asset: SearchResultItem): boolean {
    const message = asset.data as Message;
    return !!message.HasAttachments;
}
